import { RestApiClient } from 'ifield-core-components'
import NdjsonLoader from '@/lib/NdjsonLoader'
import log from 'loglevel'

/**
 * NDJSONクライアント(from URL)
 */
export default class NdjsonClient {
  /**
   * コンストラクタ
   * @param {Number} chunkSize iteration時の返却データ件数目安
   * @param {Boolean} storeCache キャッシュを貯める場合はtrue
   */
  constructor({ chunkSize, storeCache } = {}) {
    this.chunkSize = +chunkSize || 1000
    this._loader = new NdjsonLoader(this.chunkSize)
    this.storeCache = storeCache == null ? true : storeCache
    this._lastUrl = null
    this._dataCache = null
  }

  /**
   * リクエスト先URLが前回と違う場合はtrue
   * @param {URL|String} url URL
   * @returns {Boolean}
   */
  _isUrlChanged(url) {
    const stringUrl = url.toString ? url.toString() : url
    return this._lastUrl !== stringUrl
  }

  /**
   * 中断を待つ
   */
  async abort() {
    // 読み込み中の中断時点のデータは部分データであり、完全なデータとして再利用できないため
    // キャッシュを無効化する。getData()内での中断時(下記)と同じ扱いにするための対応
    // (awaitより前に同期的に無効化して、中断完了待ちとキャッシュ判定の競合を避ける)
    if (this._loader.isLoading) {
      this._lastUrl = null
    }
    await this._loader.abort()
  }

  /**
   * NDJSONデータ取得
   * @param {URL|String} url URL
   * @yields {Promise.<Array.<Object>>} データ
   */
  async *getData(url) {
    // 別の読み込みがある場合は中断する
    if (this._loader.isLoading) {
      log.debug('abort preceding request')
      await this.abort()
      this._dataCache = null
    }

    if (this.storeCache) {
      // パラメータ変更が無くキャッシュがある場合は即時返却
      // 空配列はキャッシュヒットとみなさない。0件を返した後に対象データが登録された場合、
      // 同一URLではキャッシュを返し続けて永続的に0件のままとなってしまうため
      // (インスタンスが使い回される呼び出し元ではリロードするまで復帰しない)
      if (!this._isUrlChanged(url) && this._dataCache?.length) {
        log.info('return cached data')
        yield this._dataCache
        return
      }
    }

    const response = await RestApiClient.executeGetApiInResponse(url)
    const reader = await response.body.getReader()

    // 新しく取得できそうなのでキャッシュをクリア
    this._dataCache = []

    this._lastUrl = url.toString ? url.toString() : url
    for await (const data of this._loader.load(reader)) {
      this.storeCache && this._dataCache.push(...data)
      yield data
    }
  }
}
